import { useState, useEffect, useRef } from 'react';
import { useParams } from 'react-router-dom';
import AdminLayout from '../../components/admin/AdminLayout';
import ProductFormModal from '../../components/admin/ProductFormModal';
import { apiRequest, getImageUrl } from '../../utils/api';
import {
    DndContext,
    closestCenter,
    KeyboardSensor,
    PointerSensor,
    TouchSensor,
    useSensor,
    useSensors,
} from '@dnd-kit/core';
import {
    arrayMove,
    SortableContext,
    sortableKeyboardCoordinates,
    useSortable,
    verticalListSortingStrategy,
    rectSortingStrategy,
} from '@dnd-kit/sortable';
import { CSS } from '@dnd-kit/utilities';

// Searchable Category Filter Dropdown (mobil uyumlu)
const CategoryFilterDropdown = ({ categories, value, onChange }) => {
    const [open, setOpen] = useState(false);
    const [search, setSearch] = useState('');
    const containerRef = useRef(null);

    useEffect(() => {
        const handleClickOutside = (e) => {
            if (containerRef.current && !containerRef.current.contains(e.target)) setOpen(false);
        };
        document.addEventListener('mousedown', handleClickOutside);
        return () => document.removeEventListener('mousedown', handleClickOutside);
    }, []);

    const flatOptions = [];
    const buildFlat = (parentId, level) => {
        categories
            .filter(c => (parentId === null ? !c.parent_id : c.parent_id === parentId))
            .forEach(cat => {
                flatOptions.push({ id: String(cat.id), name: cat.name, level });
                buildFlat(cat.id, level + 1);
            });
    };
    buildFlat(null, 0);

    const filtered = search.trim()
        ? flatOptions.filter(o => o.name.toLowerCase().includes(search.toLowerCase().trim()))
        : flatOptions;

    const selectedLabel = value === 'all'
        ? 'Tüm Kategoriler'
        : flatOptions.find(o => o.id === String(value))?.name || 'Kategori';

    return (
        <div ref={containerRef} className="relative">
            <button
                type="button"
                onClick={() => setOpen(o => !o)}
                className="w-full px-4 py-2.5 border border-gray-200 rounded-xl text-sm text-left bg-white focus:outline-none focus:ring-2 focus:ring-blue-500 flex items-center justify-between gap-2"
            >
                <span className="truncate">{selectedLabel}</span>
                <svg className={`w-4 h-4 text-gray-400 flex-shrink-0 transition ${open ? 'rotate-180' : ''}`} fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
                </svg>
            </button>

            {open && (
                <div className="absolute z-50 mt-1 w-full min-w-[240px] bg-white border border-gray-200 rounded-xl shadow-xl max-h-80 flex flex-col">
                    <div className="p-2 border-b border-gray-100">
                        <input
                            type="text"
                            placeholder="Kategori ara..."
                            value={search}
                            onChange={(e) => setSearch(e.target.value)}
                            className="w-full px-3 py-2 border border-gray-200 rounded-lg text-sm focus:ring-blue-500 focus:border-blue-500"
                            autoFocus
                        />
                    </div>
                    <div className="overflow-y-auto flex-1 py-1">
                        <button
                            type="button"
                            onClick={() => { onChange('all'); setOpen(false); setSearch(''); }}
                            className={`w-full px-4 py-2.5 text-sm text-left flex items-center gap-2 hover:bg-blue-50 ${value === 'all' ? 'bg-blue-50 text-blue-700 font-medium' : 'text-gray-700'}`}
                        >
                            <span className="w-5 h-5 flex items-center justify-center text-gray-400">
                                <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 6h16M4 10h16M4 14h16M4 18h16" /></svg>
                            </span>
                            Tüm Kategoriler
                            {value === 'all' && <span className="ml-auto text-blue-500">✓</span>}
                        </button>
                        {filtered.length === 0 ? (
                            <div className="px-4 py-6 text-sm text-gray-400 text-center">Sonuç bulunamadı</div>
                        ) : (
                            filtered.map(opt => (
                                <button
                                    key={opt.id}
                                    type="button"
                                    onClick={() => { onChange(opt.id); setOpen(false); setSearch(''); }}
                                    className={`w-full text-left text-sm hover:bg-blue-50 flex items-center gap-2 ${String(value) === opt.id ? 'bg-blue-50 text-blue-700 font-medium' : 'text-gray-700'}`}
                                    style={{ paddingLeft: `${16 + opt.level * 20}px`, paddingTop: '10px', paddingBottom: '10px', paddingRight: '16px' }}
                                >
                                    {opt.level > 0 && (
                                        <span className="text-gray-300 text-xs">{'└'.repeat(1)}</span>
                                    )}
                                    <span className="truncate">{opt.name}</span>
                                    {String(value) === opt.id && <span className="ml-auto text-blue-500 flex-shrink-0">✓</span>}
                                </button>
                            ))
                        )}
                    </div>
                </div>
            )}
        </div>
    );
};

// Dietary tag definitions for filters
const DIETARY_TAGS = [
    { key: 'is_vegan', label: 'Vegan', icon: '🌱' },
    { key: 'is_vegetarian', label: 'Vejetaryen', icon: '🥬' },
    { key: 'is_gluten_free', label: 'Glutensiz', icon: '🌾' },
    { key: 'is_spicy', label: 'Acı', icon: '🌶️' },
    { key: 'is_new', label: 'Yeni', icon: '✨' },
    { key: 'is_popular', label: 'Popüler', icon: '🔥' },
    { key: 'is_chef_recommended', label: 'Şef Önerisi', icon: '👨‍🍳' }
];

const ALLERGENS = [
    { key: 'gluten', label: 'Gluten' },
    { key: 'dairy', label: 'Süt' },
    { key: 'eggs', label: 'Yumurta' },
    { key: 'fish', label: 'Balık' },
    { key: 'shellfish', label: 'Kabuklu' },
    { key: 'tree_nuts', label: 'Yemiş' },
    { key: 'peanuts', label: 'Fıstık' },
    { key: 'soy', label: 'Soya' },
    { key: 'sesame', label: 'Susam' }
];

// Delete Confirmation Modal
const DeleteConfirmModal = ({ isOpen, onClose, onConfirm, productName, loading }) => {
    if (!isOpen) return null;

    return (
        <div className="fixed inset-0 z-50 overflow-y-auto">
            <div className="flex items-center justify-center min-h-screen px-4 pt-4 pb-20 text-center sm:p-0">
                <div className="fixed inset-0 transition-opacity bg-gray-500 bg-opacity-75" onClick={onClose} />
                
                <div className="relative inline-block w-full max-w-md p-6 my-8 overflow-hidden text-left align-middle transition-all transform bg-white shadow-xl rounded-2xl">
                    <div className="flex items-center justify-center w-12 h-12 mx-auto bg-red-100 rounded-full mb-4">
                        <svg className="w-6 h-6 text-red-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 7l-.867 12.142A2 2 0 0116.138 21H7.862a2 2 0 01-1.995-1.858L5 7m5 4v6m4-6v6m1-10V4a1 1 0 00-1-1h-4a1 1 0 00-1 1v3M4 7h16" />
                        </svg>
                    </div>
                    
                    <h3 className="text-lg font-bold text-center text-gray-900 mb-2">
                        Ürünü Sil
                    </h3>
                    
                    <p className="text-sm text-center text-gray-600 mb-4">
                        <span className="font-semibold text-gray-900">"{productName}"</span> ürününü silmek istediğinize emin misiniz?
                    </p>
                    
                    <p className="text-xs text-center text-gray-500 mb-6">
                        Bu işlem geri alınamaz. Ürün ve görselleri kalıcı olarak silinecektir.
                    </p>
                    
                    <div className="flex gap-3">
                        <button
                            onClick={onClose}
                            disabled={loading}
                            className="flex-1 px-4 py-2.5 text-sm font-medium text-gray-700 bg-gray-100 rounded-lg hover:bg-gray-200 transition disabled:opacity-50"
                        >
                            İptal
                        </button>
                        <button
                            onClick={onConfirm}
                            disabled={loading}
                            className="flex-1 px-4 py-2.5 text-sm font-medium text-white bg-red-600 rounded-lg hover:bg-red-700 transition disabled:opacity-50 flex items-center justify-center gap-2"
                        >
                            {loading ? (
                                <>
                                    <div className="w-4 h-4 border-2 border-white border-t-transparent rounded-full animate-spin" />
                                    Siliniyor...
                                </>
                            ) : (
                                'Evet, Sil'
                            )}
                        </button>
                    </div>
                </div>
            </div>
        </div>
    );
};

// Toggle Switch Component
const ToggleSwitch = ({ enabled, onChange, disabled }) => {
    return (
        <button
            type="button"
            onClick={onChange}
            disabled={disabled}
            className={`
                relative inline-flex h-6 w-11 flex-shrink-0 cursor-pointer rounded-full border-2 border-transparent 
                transition-colors duration-200 ease-in-out focus:outline-none focus:ring-2 focus:ring-blue-500 focus:ring-offset-2
                ${enabled ? 'bg-green-500' : 'bg-gray-300'}
                ${disabled ? 'opacity-50 cursor-not-allowed' : ''}
            `}
        >
            <span
                className={`
                    pointer-events-none inline-block h-5 w-5 transform rounded-full bg-white shadow ring-0 
                    transition duration-200 ease-in-out
                    ${enabled ? 'translate-x-5' : 'translate-x-0'}
                `}
            />
        </button>
    );
};

// Product Tags/Badges Component
const ProductBadges = ({ product, size = 'sm' }) => {
    const tags = [];
    
    if (product.is_vegan) tags.push({ icon: '🌱', label: 'Vegan', color: 'bg-green-100 text-green-700' });
    if (product.is_vegetarian) tags.push({ icon: '🥬', label: 'Vejetaryen', color: 'bg-green-50 text-green-600' });
    if (product.is_gluten_free) tags.push({ icon: '🌾', label: 'Glutensiz', color: 'bg-yellow-100 text-yellow-700' });
    if (product.is_spicy) tags.push({ icon: '🌶️', label: `Acı${product.spicy_level ? ` (${product.spicy_level})` : ''}`, color: 'bg-red-100 text-red-700' });
    if (product.is_new) tags.push({ icon: '✨', label: 'Yeni', color: 'bg-blue-100 text-blue-700' });
    if (product.is_popular) tags.push({ icon: '🔥', label: 'Popüler', color: 'bg-orange-100 text-orange-700' });
    if (product.is_chef_recommended) tags.push({ icon: '👨‍🍳', label: 'Şef', color: 'bg-purple-100 text-purple-700' });
    
    if (tags.length === 0) return null;
    
    return (
        <div className="flex flex-wrap gap-1">
            {tags.map((tag, idx) => (
                <span 
                    key={idx} 
                    className={`inline-flex items-center gap-0.5 px-1.5 py-0.5 rounded text-xs font-medium ${tag.color}`}
                    title={tag.label}
                >
                    <span>{tag.icon}</span>
                    {size !== 'xs' && <span className="hidden sm:inline">{tag.label}</span>}
                </span>
            ))}
        </div>
    );
};

// Nutrition Info Component
const NutritionInfo = ({ product }) => {
    const hasNutrition = product.calories || product.protein || product.carbs || product.fat;
    if (!hasNutrition) return null;
    
    return (
        <div className="flex flex-wrap gap-2 text-xs text-gray-500">
            {product.calories && (
                <span className="bg-gray-100 px-1.5 py-0.5 rounded">{product.calories} kcal</span>
            )}
            {product.protein && (
                <span className="bg-blue-50 text-blue-600 px-1.5 py-0.5 rounded">P: {product.protein}g</span>
            )}
            {product.carbs && (
                <span className="bg-amber-50 text-amber-600 px-1.5 py-0.5 rounded">K: {product.carbs}g</span>
            )}
            {product.fat && (
                <span className="bg-red-50 text-red-600 px-1.5 py-0.5 rounded">Y: {product.fat}g</span>
            )}
        </div>
    );
};

// Allergens Info Component
const AllergensInfo = ({ product }) => {
    const allergens = product.allergens || [];
    if (allergens.length === 0) return null;
    
    const allergenLabels = {
        gluten: 'Gluten', dairy: 'Süt', eggs: 'Yumurta', fish: 'Balık',
        shellfish: 'Kabuklu', tree_nuts: 'Yemiş', peanuts: 'Fıstık', 
        soy: 'Soya', sesame: 'Susam', celery: 'Kereviz', mustard: 'Hardal', sulfites: 'Sülfit'
    };
    
    return (
        <div className="flex flex-wrap gap-1">
            <span className="text-xs text-red-500 font-medium">⚠️</span>
            {allergens.slice(0, 3).map((a, idx) => (
                <span key={idx} className="text-xs text-red-600 bg-red-50 px-1.5 py-0.5 rounded">
                    {allergenLabels[a] || a}
                </span>
            ))}
            {allergens.length > 3 && (
                <span className="text-xs text-red-500">+{allergens.length - 3}</span>
            )}
        </div>
    );
};

// Drag Handle Icon
const DragHandleIcon = ({ className = "w-5 h-5" }) => (
    <svg className={className} fill="none" stroke="currentColor" viewBox="0 0 24 24">
        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 8h16M4 16h16" />
    </svg>
);

// Arrow Up Icon
const ArrowUpIcon = ({ className = "w-4 h-4" }) => (
    <svg className={className} fill="none" stroke="currentColor" viewBox="0 0 24 24">
        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 15l7-7 7 7" />
    </svg>
);

// Arrow Down Icon
const ArrowDownIcon = ({ className = "w-4 h-4" }) => (
    <svg className={className} fill="none" stroke="currentColor" viewBox="0 0 24 24">
        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
    </svg>
);

// Sortable Product Item Component
const SortableProductItem = ({ 
    product, 
    index,
    total,
    onEdit,
    onDelete,
    onToggleStatus,
    onMoveUp,
    onMoveDown,
    onSortOrderChange,
    togglingStatus,
    savingOrder,
    renderProductCard
}) => {
    const {
        attributes,
        listeners,
        setNodeRef,
        transform,
        transition,
        isDragging,
    } = useSortable({ id: product.id });

    const style = {
        transform: CSS.Transform.toString(transform),
        transition,
        opacity: isDragging ? 0.5 : 1,
        zIndex: isDragging ? 1000 : 'auto',
    };

    const [editingSortOrder, setEditingSortOrder] = useState(false);
    const [sortOrderValue, setSortOrderValue] = useState(product.sort_order || 0);

    const handleSortOrderSubmit = () => {
        const newOrder = parseInt(sortOrderValue, 10);
        if (!isNaN(newOrder) && newOrder !== product.sort_order) {
            onSortOrderChange(product, newOrder);
        }
        setEditingSortOrder(false);
    };

    const productCard = renderProductCard(product);
    
    return (
        <div ref={setNodeRef} style={style} className={isDragging ? 'opacity-50' : ''}>
            <div className="relative group">
                {/* Drag Controls - Overlay on hover */}
                <div className="absolute left-2 top-2 z-10 flex flex-col gap-1 opacity-0 group-hover:opacity-100 transition-opacity">
                    <button
                        {...attributes}
                        {...listeners}
                        className="p-1.5 rounded-lg bg-white shadow-md text-gray-500 hover:bg-gray-50 hover:text-gray-700 cursor-grab active:cursor-grabbing"
                        title="Sürükle"
                    >
                        <DragHandleIcon className="w-4 h-4" />
                    </button>
                    <button
                        onClick={() => onMoveUp(product, index)}
                        disabled={index === 0 || savingOrder}
                        className="p-1 rounded bg-white shadow-md text-gray-400 hover:text-blue-600 hover:bg-blue-50 disabled:opacity-30 disabled:cursor-not-allowed"
                        title="Yukarı"
                    >
                        <ArrowUpIcon className="w-3 h-3" />
                    </button>
                    <button
                        onClick={() => onMoveDown(product, index)}
                        disabled={index === total - 1 || savingOrder}
                        className="p-1 rounded bg-white shadow-md text-gray-400 hover:text-blue-600 hover:bg-blue-50 disabled:opacity-30 disabled:cursor-not-allowed"
                        title="Aşağı"
                    >
                        <ArrowDownIcon className="w-3 h-3" />
                    </button>
                    <button
                        onClick={() => setEditingSortOrder(true)}
                        disabled={savingOrder}
                        className="p-1 rounded bg-white shadow-md text-gray-400 hover:text-green-600 hover:bg-green-50 disabled:opacity-30 disabled:cursor-not-allowed text-xs font-medium"
                        title="Sıra No"
                    >
                        {product.sort_order || 0}
                    </button>
                </div>
                
                {/* Sort Order Input Modal */}
                {editingSortOrder && (
                    <div className="absolute left-2 top-16 z-20 bg-white rounded-lg shadow-xl border p-3">
                        <input
                            type="number"
                            value={sortOrderValue}
                            onChange={(e) => setSortOrderValue(e.target.value)}
                            onKeyDown={(e) => {
                                if (e.key === 'Enter') handleSortOrderSubmit();
                                if (e.key === 'Escape') setEditingSortOrder(false);
                            }}
                            onBlur={handleSortOrderSubmit}
                            className="w-20 px-2 py-1 border rounded text-sm"
                            autoFocus
                        />
                    </div>
                )}
                
                {productCard}
            </div>
        </div>
    );
};

const ProductsPage = () => {
    const { organizationId } = useParams();
    const [products, setProducts] = useState([]);
    const [categories, setCategories] = useState([]);
    const [loading, setLoading] = useState(true);
    const [showModal, setShowModal] = useState(false);
    const [editingProduct, setEditingProduct] = useState(null);
    
    // Filters
    const [selectedCategory, setSelectedCategory] = useState('all');
    const [searchTerm, setSearchTerm] = useState('');
    const [statusFilter, setStatusFilter] = useState('all');
    const [viewMode, setViewMode] = useState('list'); // 'category' | 'grid' | 'list'
    const [showAdvancedFilters, setShowAdvancedFilters] = useState(false);
    const [dietaryFilters, setDietaryFilters] = useState([]);
    const [allergenFilters, setAllergenFilters] = useState([]);
    const [hasNutritionFilter, setHasNutritionFilter] = useState(false);
    
    // Delete modal
    const [deleteModal, setDeleteModal] = useState({ isOpen: false, product: null });
    const [deleting, setDeleting] = useState(false);
    
    // Toggle status
    const [togglingStatus, setTogglingStatus] = useState(null);
    
    // Sorting
    const [savingOrder, setSavingOrder] = useState(false);
    
    // DnD sensors
    const sensors = useSensors(
        useSensor(PointerSensor),
        useSensor(KeyboardSensor, {
            coordinateGetter: sortableKeyboardCoordinates,
        }),
        useSensor(TouchSensor)
    );

    useEffect(() => {
        loadCategories();
        loadProducts();
    }, [organizationId]);

    const loadCategories = async () => {
        try {
            const response = await apiRequest(`/organizations/${organizationId}/categories`);
            const sorted = (response.data || []).sort((a, b) => (a.sort_order || 0) - (b.sort_order || 0));
            setCategories(sorted);
        } catch (error) {
            console.error('Load categories error:', error);
        }
    };

    const loadProducts = async () => {
        setLoading(true);
        try {
            // Always include images
            const response = await apiRequest(`/organizations/${organizationId}/products?include_images=true`);
            setProducts(response.data || []);
        } catch (error) {
            console.error('Load products error:', error);
        } finally {
            setLoading(false);
        }
    };

    // Filter products
    const filteredProducts = products.filter(product => {
        // Category filter
        if (selectedCategory !== 'all' && product.category_id !== parseInt(selectedCategory)) {
            return false;
        }
        
        // Status filter
        if (statusFilter !== 'all' && product.status !== statusFilter) {
            return false;
        }
        
        // Search filter
        if (searchTerm) {
            const term = searchTerm.toLowerCase();
            if (!product.name.toLowerCase().includes(term) && 
                !product.description?.toLowerCase().includes(term)) {
                return false;
            }
        }
        
        // Dietary filters
        if (dietaryFilters.length > 0) {
            const hasAllTags = dietaryFilters.every(tag => product[tag]);
            if (!hasAllTags) return false;
        }
        
        // Allergen filters (products WITHOUT these allergens)
        if (allergenFilters.length > 0) {
            const productAllergens = product.allergens || [];
            const hasExcludedAllergen = allergenFilters.some(a => productAllergens.includes(a));
            if (hasExcludedAllergen) return false;
        }
        
        // Has nutrition info filter
        if (hasNutritionFilter) {
            if (!product.calories && !product.protein && !product.carbs && !product.fat) {
                return false;
            }
        }
        
        return true;
    });

    // Sort: en son eklenen en üstte (created_at DESC), sort_order varsa öncelikli
    const sortedProducts = [...filteredProducts].sort((a, b) => {
        return new Date(b.created_at) - new Date(a.created_at);
    });

    // Kategori görünümlerinde sort_order kullan
    const sortByOrder = (a, b) => {
        const sortOrderCompare = (a.sort_order || 0) - (b.sort_order || 0);
        if (sortOrderCompare !== 0) return sortOrderCompare;
        return new Date(b.created_at) - new Date(a.created_at);
    };

    // Group products by category (sorted by sort_order within each category)
    const productsByCategory = categories.reduce((acc, category) => {
        const categoryProducts = filteredProducts
            .filter(p => p.category_id === category.id)
            .sort(sortByOrder);
        if (categoryProducts.length > 0) {
            acc.push({
                category,
                products: categoryProducts
            });
        }
        return acc;
    }, []);

    // Uncategorized products (sorted by sort_order)
    const uncategorizedProducts = filteredProducts
        .filter(p => !p.category_id)
        .sort(sortByOrder);

    // Toggle dietary filter
    const toggleDietaryFilter = (key) => {
        setDietaryFilters(prev => 
            prev.includes(key) ? prev.filter(k => k !== key) : [...prev, key]
        );
    };

    // Toggle allergen filter
    const toggleAllergenFilter = (key) => {
        setAllergenFilters(prev => 
            prev.includes(key) ? prev.filter(k => k !== key) : [...prev, key]
        );
    };

    // Clear all filters
    const clearFilters = () => {
        setSelectedCategory('all');
        setSearchTerm('');
        setStatusFilter('all');
        setDietaryFilters([]);
        setAllergenFilters([]);
        setHasNutritionFilter(false);
    };

    // Toggle status
    const handleToggleStatus = async (product) => {
        setTogglingStatus(product.id);
        try {
            const response = await apiRequest(`/organizations/${organizationId}/products/${product.id}/toggle-status`, {
                method: 'PATCH'
            });
            setProducts(prev => prev.map(p => 
                p.id === product.id ? { ...p, status: response.data.status } : p
            ));
        } catch (error) {
            console.error('Toggle status error:', error);
            alert('Durum güncellenirken bir hata oluştu');
        } finally {
            setTogglingStatus(null);
        }
    };

    // Delete handlers
    const openDeleteModal = (product) => {
        setDeleteModal({ isOpen: true, product });
    };

    const closeDeleteModal = () => {
        setDeleteModal({ isOpen: false, product: null });
    };

    const handleConfirmDelete = async () => {
        if (!deleteModal.product) return;
        
        setDeleting(true);
        try {
            await apiRequest(`/organizations/${organizationId}/products/${deleteModal.product.id}`, {
                method: 'DELETE'
            });
            closeDeleteModal();
            loadProducts();
        } catch (error) {
            console.error('Delete error:', error);
            alert('Ürün silinirken bir hata oluştu');
        } finally {
            setDeleting(false);
        }
    };

    // Get category name
    const getCategoryName = (categoryId) => {
        const category = categories.find(c => c.id === categoryId);
        return category ? category.name : 'Kategorisiz';
    };

    // Handle product reorder (drag and drop)
    const handleProductDragEnd = async (categoryId, products, oldIndex, newIndex) => {
        if (oldIndex === newIndex) return;
        
        const reorderedProducts = arrayMove(products, oldIndex, newIndex);
        
        // Update sort_order for all products in this category
        const updates = reorderedProducts.map((product, index) => ({
            id: product.id,
            sort_order: index + 1
        }));

        // Update local state immediately for better UX
        setProducts(prev => {
            const newProducts = [...prev];
            updates.forEach(update => {
                const idx = newProducts.findIndex(p => p.id === update.id);
                if (idx !== -1) {
                    newProducts[idx] = { ...newProducts[idx], sort_order: update.sort_order };
                }
            });
            return newProducts;
        });

        setSavingOrder(true);
        try {
            const response = await apiRequest(`/organizations/${organizationId}/products/reorder`, {
                method: 'PUT',
                body: { products: updates }
            });
            
            if (response.success) {
                // Reload products to ensure consistency
                await loadProducts();
            }
        } catch (error) {
            console.error('Failed to save product order:', error);
            alert('Sıralama güncellenirken bir hata oluştu. Lütfen tekrar deneyin.');
            // Reload to restore correct order
            await loadProducts();
        } finally {
            setSavingOrder(false);
        }
    };

    // Handle move up
    const handleMoveUp = (product, index, categoryProducts) => {
        if (index === 0) return;
        handleProductDragEnd(product.category_id, categoryProducts, index, index - 1);
    };

    // Handle move down
    const handleMoveDown = (product, index, categoryProducts) => {
        if (index === categoryProducts.length - 1) return;
        handleProductDragEnd(product.category_id, categoryProducts, index, index + 1);
    };

    // Handle sort order change
    const handleSortOrderChange = async (product, newOrder) => {
        const newSortOrder = parseInt(newOrder, 10);
        if (isNaN(newSortOrder) || newSortOrder < 0) {
            alert('Geçerli bir sıra numarası giriniz.');
            return;
        }
        
        const updates = [{ id: product.id, sort_order: newSortOrder }];
        
        // Update local state immediately
        setProducts(prev => prev.map(p => 
            p.id === product.id ? { ...p, sort_order: newSortOrder } : p
        ));

        setSavingOrder(true);
        try {
            const response = await apiRequest(`/organizations/${organizationId}/products/reorder`, {
                method: 'PUT',
                body: { products: updates }
            });
            
            if (response.success) {
                // Reload products to ensure consistency
                await loadProducts();
            }
        } catch (error) {
            console.error('Failed to save product order:', error);
            alert('Sıralama güncellenirken bir hata oluştu. Lütfen tekrar deneyin.');
            // Reload to restore correct order
            await loadProducts();
        } finally {
            setSavingOrder(false);
        }
    };

    // Render product card
    const renderProductCard = (product, isGridView = false) => {
        const primaryImage = product.images?.find(img => img.is_primary) || product.images?.[0];
        
        return (
            <div 
                key={product.id} 
                className={`bg-white rounded-xl border ${product.status === 'active' ? 'border-gray-100' : 'border-gray-200 bg-gray-50 opacity-75'} shadow-sm hover:shadow-md transition-all`}
            >
                {/* Image */}
                <div className="relative aspect-[4/3] overflow-hidden rounded-t-xl bg-gray-100">
                    {primaryImage ? (
                        <img
                            src={getImageUrl(primaryImage.image_url)}
                            alt={product.name}
                            className="w-full h-full object-cover"
                            onError={(e) => {
                                e.target.onerror = null;
                                e.target.src = '';
                                e.target.parentElement.innerHTML = `
                                    <div class="w-full h-full flex items-center justify-center text-gray-300">
                                        <svg class="w-16 h-16" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                            <path stroke-linecap="round" stroke-linejoin="round" stroke-width="1" d="M4 16l4.586-4.586a2 2 0 012.828 0L16 16m-2-2l1.586-1.586a2 2 0 012.828 0L20 14m-6-6h.01M6 20h12a2 2 0 002-2V6a2 2 0 00-2-2H6a2 2 0 00-2 2v12a2 2 0 002 2z" />
                                        </svg>
                                    </div>
                                `;
                            }}
                        />
                    ) : (
                        <div className="w-full h-full flex items-center justify-center text-gray-300">
                            <svg className="w-16 h-16" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1} d="M4 16l4.586-4.586a2 2 0 012.828 0L16 16m-2-2l1.586-1.586a2 2 0 012.828 0L20 14m-6-6h.01M6 20h12a2 2 0 002-2V6a2 2 0 00-2-2H6a2 2 0 00-2 2v12a2 2 0 002 2z" />
                            </svg>
                        </div>
                    )}
                    
                    {/* Price Badge */}
                    <div className="absolute top-2 right-2 bg-white/95 backdrop-blur px-2 py-1 rounded-lg shadow">
                        {product.is_discount_active && product.discount_price ? (
                            <div className="flex flex-col items-end">
                                <span className="text-xs text-gray-400 line-through">{parseFloat(product.price).toFixed(2)}₺</span>
                                <span className="font-bold text-green-600">{parseFloat(product.discount_price).toFixed(2)}₺</span>
                            </div>
                        ) : (
                            <span className="font-bold text-gray-900">{parseFloat(product.price).toFixed(2)}₺</span>
                        )}
                    </div>
                    
                    {/* Discount Badge */}
                    {product.is_discount_active && product.discount_percentage && (
                        <div className="absolute top-2 left-2 bg-green-500 text-white text-xs font-bold px-2 py-1 rounded-lg shadow">
                            %{product.discount_percentage} İNDİRİM
                        </div>
                    )}
                    
                    {/* Image count badge */}
                    {product.images?.length > 1 && (
                        <div className="absolute bottom-2 left-2 bg-black/60 text-white text-xs px-2 py-1 rounded">
                            📷 {product.images.length}
                        </div>
                    )}
                </div>
                
                {/* Content */}
                <div className="p-3">
                    <h3 className="font-semibold text-gray-900 mb-1 line-clamp-1">{product.name}</h3>
                    
                    {/* Category badge for grid view */}
                    {isGridView && (
                        <p className="text-xs text-gray-500 mb-2">{getCategoryName(product.category_id)}</p>
                    )}
                    
                    {/* Dietary Tags */}
                    <div className="mb-2">
                        <ProductBadges product={product} size="xs" />
                    </div>
                    
                    {/* Nutrition */}
                    <div className="mb-2">
                        <NutritionInfo product={product} />
                    </div>
                    
                    {/* Allergens */}
                    <div className="mb-3">
                        <AllergensInfo product={product} />
                    </div>
                    
                    {/* Prep time */}
                    {product.preparation_time && (
                        <p className="text-xs text-gray-500 mb-2">⏱️ {product.preparation_time} dk</p>
                    )}
                    
                    {/* Status Toggle */}
                    <div className="flex items-center justify-between mb-3">
                        <div className="flex items-center gap-2">
                            <ToggleSwitch
                                enabled={product.status === 'active'}
                                onChange={() => handleToggleStatus(product)}
                                disabled={togglingStatus === product.id}
                            />
                            <span className={`text-xs font-medium ${product.status === 'active' ? 'text-green-600' : 'text-gray-500'}`}>
                                {product.status === 'active' ? 'Aktif' : 'Pasif'}
                            </span>
                        </div>
                    </div>
                    
                    {/* Actions */}
                    <div className="flex gap-2">
                        <button
                            onClick={() => {
                                setEditingProduct(product);
                                setShowModal(true);
                            }}
                            className="flex-1 px-3 py-2 text-sm font-medium text-blue-600 bg-blue-50 rounded-lg hover:bg-blue-100 transition"
                        >
                            Düzenle
                        </button>
                        <button
                            onClick={() => openDeleteModal(product)}
                            className="px-3 py-2 text-sm font-medium text-red-600 bg-red-50 rounded-lg hover:bg-red-100 transition"
                        >
                            <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 7l-.867 12.142A2 2 0 0116.138 21H7.862a2 2 0 01-1.995-1.858L5 7m5 4v6m4-6v6m1-10V4a1 1 0 00-1-1h-4a1 1 0 00-1 1v3M4 7h16" />
                            </svg>
                        </button>
                    </div>
                </div>
            </div>
        );
    };

    // Render product row (list view)
    const renderProductRow = (product) => {
        const primaryImage = product.images?.find(img => img.is_primary) || product.images?.[0];
        
        return (
            <div 
                key={product.id}
                className={`flex items-center gap-4 p-4 bg-white rounded-xl border ${product.status === 'active' ? 'border-gray-100' : 'border-gray-200 bg-gray-50 opacity-75'} shadow-sm`}
            >
                {/* Image */}
                <div className="w-16 h-16 flex-shrink-0 rounded-lg overflow-hidden bg-gray-100">
                    {primaryImage ? (
                        <img
                            src={getImageUrl(primaryImage.image_url)}
                            alt={product.name}
                            className="w-full h-full object-cover"
                            onError={(e) => {
                                e.target.onerror = null;
                                e.target.style.display = 'none';
                            }}
                        />
                    ) : (
                        <div className="w-full h-full flex items-center justify-center text-gray-300">
                            <svg className="w-8 h-8" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1} d="M4 16l4.586-4.586a2 2 0 012.828 0L16 16m-2-2l1.586-1.586a2 2 0 012.828 0L20 14m-6-6h.01M6 20h12a2 2 0 002-2V6a2 2 0 00-2-2H6a2 2 0 00-2 2v12a2 2 0 002 2z" />
                            </svg>
                        </div>
                    )}
                </div>
                
                {/* Info */}
                <div className="flex-1 min-w-0">
                    <h3 className="font-semibold text-gray-900 truncate">{product.name}</h3>
                    <p className="text-sm text-gray-500">{getCategoryName(product.category_id)}</p>
                    <div className="flex items-center gap-2 mt-1">
                        <ProductBadges product={product} size="xs" />
                    </div>
                </div>
                
                {/* Nutrition & Allergens */}
                <div className="hidden md:flex flex-col gap-1">
                    <NutritionInfo product={product} />
                    <AllergensInfo product={product} />
                </div>
                
                {/* Price */}
                <div className="text-right">
                    {product.is_discount_active && product.discount_price ? (
                        <div className="flex flex-col items-end">
                            <span className="text-xs text-gray-400 line-through">{parseFloat(product.price).toFixed(2)}₺</span>
                            <div className="flex items-center gap-1">
                                <span className="font-bold text-green-600">{parseFloat(product.discount_price).toFixed(2)}₺</span>
                                <span className="text-xs bg-green-100 text-green-700 px-1 rounded">%{product.discount_percentage}</span>
                            </div>
                        </div>
                    ) : (
                        <span className="font-bold text-gray-900">{parseFloat(product.price).toFixed(2)}₺</span>
                    )}
                    {product.preparation_time && (
                        <p className="text-xs text-gray-500">⏱️ {product.preparation_time}dk</p>
                    )}
                </div>
                
                {/* Toggle */}
                <ToggleSwitch
                    enabled={product.status === 'active'}
                    onChange={() => handleToggleStatus(product)}
                    disabled={togglingStatus === product.id}
                />
                
                {/* Actions */}
                <div className="flex gap-2">
                    <button
                        onClick={() => {
                            setEditingProduct(product);
                            setShowModal(true);
                        }}
                        className="p-2 text-blue-600 hover:bg-blue-50 rounded-lg transition"
                    >
                        <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M11 5H6a2 2 0 00-2 2v11a2 2 0 002 2h11a2 2 0 002-2v-5m-1.414-9.414a2 2 0 112.828 2.828L11.828 15H9v-2.828l8.586-8.586z" />
                        </svg>
                    </button>
                    <button
                        onClick={() => openDeleteModal(product)}
                        className="p-2 text-red-600 hover:bg-red-50 rounded-lg transition"
                    >
                        <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 7l-.867 12.142A2 2 0 0116.138 21H7.862a2 2 0 01-1.995-1.858L5 7m5 4v6m4-6v6m1-10V4a1 1 0 00-1-1h-4a1 1 0 00-1 1v3M4 7h16" />
                        </svg>
                    </button>
                </div>
            </div>
        );
    };

    const hasActiveFilters = selectedCategory !== 'all' || searchTerm || statusFilter !== 'all' || 
        dietaryFilters.length > 0 || allergenFilters.length > 0 || hasNutritionFilter;

    return (
        <AdminLayout>
            <div className="max-w-7xl mx-auto">
                {/* Header */}
                <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4 mb-6">
                    <div>
                        <h1 className="text-2xl sm:text-3xl font-bold text-gray-900">Ürünler</h1>
                        <p className="text-gray-500 text-sm mt-1">
                            {sortedProducts.length} ürün {selectedCategory !== 'all' && `(${getCategoryName(parseInt(selectedCategory))})`}
                        </p>
                    </div>
                    <button
                        onClick={() => {
                            setEditingProduct(null);
                            setShowModal(true);
                        }}
                        className="w-full sm:w-auto px-4 py-2.5 bg-blue-600 text-white rounded-xl hover:bg-blue-700 transition flex items-center justify-center gap-2 font-medium shadow-lg shadow-blue-600/25"
                    >
                        <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 4v16m8-8H4" />
                        </svg>
                        Yeni Ürün
                    </button>
                </div>

                {/* Filters */}
                <div className="bg-white rounded-2xl shadow-sm border border-gray-100 p-4 mb-6">
                    <div className="flex flex-col gap-4">
                        {/* Main Filters Row */}
                        <div className="flex flex-col lg:flex-row gap-4">
                            {/* Search */}
                            <div className="flex-1">
                                <div className="relative">
                                    <input
                                        type="text"
                                        placeholder="Ürün ara..."
                                        value={searchTerm}
                                        onChange={(e) => setSearchTerm(e.target.value)}
                                        className="w-full pl-10 pr-4 py-2.5 border border-gray-200 rounded-xl focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                                    />
                                    <svg className="absolute left-3 top-1/2 transform -translate-y-1/2 w-5 h-5 text-gray-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0z" />
                                    </svg>
                                </div>
                            </div>
                            
                            {/* Category Filter */}
                            <div className="w-full lg:w-56">
                                <CategoryFilterDropdown
                                    categories={categories}
                                    value={selectedCategory}
                                    onChange={setSelectedCategory}
                                />
                            </div>
                            
                            {/* Status Filter */}
                            <div className="w-full lg:w-36">
                                <select
                                    value={statusFilter}
                                    onChange={(e) => setStatusFilter(e.target.value)}
                                    className="w-full px-4 py-2.5 border border-gray-200 rounded-xl focus:outline-none focus:ring-2 focus:ring-blue-500 bg-white"
                                >
                                    <option value="all">Tüm Durum</option>
                                    <option value="active">Aktif</option>
                                    <option value="inactive">Pasif</option>
                                </select>
                            </div>
                            
                            {/* Advanced Filters Toggle */}
                            <button
                                onClick={() => setShowAdvancedFilters(!showAdvancedFilters)}
                                className={`px-4 py-2.5 rounded-xl border transition flex items-center gap-2 ${
                                    showAdvancedFilters || dietaryFilters.length > 0 || allergenFilters.length > 0
                                        ? 'bg-blue-50 border-blue-200 text-blue-600'
                                        : 'border-gray-200 text-gray-600 hover:bg-gray-50'
                                }`}
                            >
                                <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M3 4a1 1 0 011-1h16a1 1 0 011 1v2.586a1 1 0 01-.293.707l-6.414 6.414a1 1 0 00-.293.707V17l-4 4v-6.586a1 1 0 00-.293-.707L3.293 7.293A1 1 0 013 6.586V4z" />
                                </svg>
                                <span className="hidden sm:inline">Gelişmiş</span>
                                {(dietaryFilters.length > 0 || allergenFilters.length > 0) && (
                                    <span className="w-5 h-5 bg-blue-600 text-white text-xs rounded-full flex items-center justify-center">
                                        {dietaryFilters.length + allergenFilters.length}
                                    </span>
                                )}
                            </button>
                            
                            {/* View Mode */}
                            <div className="flex gap-1 bg-gray-100 p-1 rounded-xl">
                                <button
                                    onClick={() => setViewMode('category')}
                                    className={`p-2 rounded-lg transition ${viewMode === 'category' ? 'bg-white shadow text-blue-600' : 'text-gray-500 hover:text-gray-700'}`}
                                    title="Kategori Görünümü"
                                >
                                    <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 11H5m14 0a2 2 0 012 2v6a2 2 0 01-2 2H5a2 2 0 01-2-2v-6a2 2 0 012-2m14 0V9a2 2 0 00-2-2M5 11V9a2 2 0 012-2m0 0V5a2 2 0 012-2h6a2 2 0 012 2v2M7 7h10" />
                                    </svg>
                                </button>
                                <button
                                    onClick={() => setViewMode('grid')}
                                    className={`p-2 rounded-lg transition ${viewMode === 'grid' ? 'bg-white shadow text-blue-600' : 'text-gray-500 hover:text-gray-700'}`}
                                    title="Grid Görünümü"
                                >
                                    <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 6a2 2 0 012-2h2a2 2 0 012 2v2a2 2 0 01-2 2H6a2 2 0 01-2-2V6zM14 6a2 2 0 012-2h2a2 2 0 012 2v2a2 2 0 01-2 2h-2a2 2 0 01-2-2V6zM4 16a2 2 0 012-2h2a2 2 0 012 2v2a2 2 0 01-2 2H6a2 2 0 01-2-2v-2zM14 16a2 2 0 012-2h2a2 2 0 012 2v2a2 2 0 01-2 2h-2a2 2 0 01-2-2v-2z" />
                                    </svg>
                                </button>
                                <button
                                    onClick={() => setViewMode('list')}
                                    className={`p-2 rounded-lg transition ${viewMode === 'list' ? 'bg-white shadow text-blue-600' : 'text-gray-500 hover:text-gray-700'}`}
                                    title="Liste Görünümü"
                                >
                                    <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 6h16M4 10h16M4 14h16M4 18h16" />
                                    </svg>
                                </button>
                            </div>
                        </div>
                        
                        {/* Advanced Filters */}
                        {showAdvancedFilters && (
                            <div className="border-t border-gray-100 pt-4 space-y-4">
                                {/* Dietary Tags */}
                                <div>
                                    <h4 className="text-sm font-medium text-gray-700 mb-2">Ürün Özellikleri</h4>
                                    <div className="flex flex-wrap gap-2">
                                        {DIETARY_TAGS.map(tag => (
                                            <button
                                                key={tag.key}
                                                onClick={() => toggleDietaryFilter(tag.key)}
                                                className={`px-3 py-1.5 text-sm rounded-lg border transition ${
                                                    dietaryFilters.includes(tag.key)
                                                        ? 'bg-blue-50 border-blue-200 text-blue-700'
                                                        : 'bg-gray-50 border-gray-200 text-gray-600 hover:bg-gray-100'
                                                }`}
                                            >
                                                {tag.icon} {tag.label}
                                            </button>
                                        ))}
                                    </div>
                                </div>
                                
                                {/* Allergen Exclusions */}
                                <div>
                                    <h4 className="text-sm font-medium text-gray-700 mb-2">Alerjen İçermeyen (Hariç Tut)</h4>
                                    <div className="flex flex-wrap gap-2">
                                        {ALLERGENS.map(allergen => (
                                            <button
                                                key={allergen.key}
                                                onClick={() => toggleAllergenFilter(allergen.key)}
                                                className={`px-3 py-1.5 text-sm rounded-lg border transition ${
                                                    allergenFilters.includes(allergen.key)
                                                        ? 'bg-red-50 border-red-200 text-red-700'
                                                        : 'bg-gray-50 border-gray-200 text-gray-600 hover:bg-gray-100'
                                                }`}
                                            >
                                                ⚠️ {allergen.label}
                                            </button>
                                        ))}
                                    </div>
                                </div>
                                
                                {/* Nutrition Filter */}
                                <div className="flex items-center gap-3">
                                    <label className="flex items-center gap-2 cursor-pointer">
                                        <input
                                            type="checkbox"
                                            checked={hasNutritionFilter}
                                            onChange={(e) => setHasNutritionFilter(e.target.checked)}
                                            className="w-4 h-4 rounded border-gray-300 text-blue-600 focus:ring-blue-500"
                                        />
                                        <span className="text-sm text-gray-700">Sadece besin değeri olanlar</span>
                                    </label>
                                </div>
                                
                                {/* Clear Filters */}
                                {hasActiveFilters && (
                                    <button
                                        onClick={clearFilters}
                                        className="text-sm text-red-600 hover:text-red-700 font-medium"
                                    >
                                        ✕ Tüm filtreleri temizle
                                    </button>
                                )}
                            </div>
                        )}
                    </div>
                </div>

                {/* Content */}
                {loading ? (
                    <div className="flex items-center justify-center py-12">
                        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600"></div>
                    </div>
                ) : sortedProducts.length === 0 ? (
                    <div className="text-center py-12 bg-white rounded-2xl shadow-sm border border-gray-100">
                        <div className="w-20 h-20 bg-gray-100 rounded-full flex items-center justify-center mx-auto mb-4">
                            <svg className="w-10 h-10 text-gray-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M20 7l-8-4-8 4m16 0l-8 4m8-4v10l-8 4m0-10L4 7m8 4v10M4 7v10l8 4" />
                            </svg>
                        </div>
                        <p className="text-gray-500 mb-4">
                            {hasActiveFilters
                                ? 'Filtrelere uygun ürün bulunamadı'
                                : 'Henüz ürün eklenmemiş'}
                        </p>
                        {hasActiveFilters ? (
                            <button
                                onClick={clearFilters}
                                className="px-6 py-2.5 bg-gray-100 text-gray-700 rounded-xl hover:bg-gray-200 transition font-medium"
                            >
                                Filtreleri Temizle
                            </button>
                        ) : (
                            <button
                                onClick={() => setShowModal(true)}
                                className="px-6 py-2.5 bg-blue-600 text-white rounded-xl hover:bg-blue-700 transition font-medium"
                            >
                                İlk Ürünü Ekle
                            </button>
                        )}
                    </div>
                ) : viewMode === 'category' ? (
                    // Category View - Single column with drag and drop
                    <div className="space-y-8">
                        {productsByCategory.map(({ category, products: categoryProducts }) => {
                            const productIds = categoryProducts.map(p => p.id);
                            
                            const handleDragEnd = (event) => {
                                const { active, over } = event;
                                if (!over || active.id === over.id) return;

                                const oldIndex = categoryProducts.findIndex(p => p.id === active.id);
                                const newIndex = categoryProducts.findIndex(p => p.id === over.id);

                                if (oldIndex !== -1 && newIndex !== -1) {
                                    handleProductDragEnd(category.id, categoryProducts, oldIndex, newIndex);
                                }
                            };

                            return (
                                <div key={category.id}>
                                    <div className="flex items-center gap-3 mb-4">
                                        <h2 className="text-lg font-bold text-gray-900">{category.name}</h2>
                                        <span className="px-2 py-0.5 bg-gray-100 text-gray-600 text-sm rounded-full">
                                            {categoryProducts.length} ürün
                                        </span>
                                    </div>
                                    <DndContext
                                        sensors={sensors}
                                        collisionDetection={closestCenter}
                                        onDragEnd={handleDragEnd}
                                    >
                                        <SortableContext items={productIds} strategy={verticalListSortingStrategy}>
                                            <div className="grid grid-cols-1 gap-4">
                                                {categoryProducts.map((product, index) => (
                                                    <SortableProductItem
                                                        key={product.id}
                                                        product={product}
                                                        index={index}
                                                        total={categoryProducts.length}
                                                        onEdit={(p) => {
                                                            setEditingProduct(p);
                                                            setShowModal(true);
                                                        }}
                                                        onDelete={openDeleteModal}
                                                        onToggleStatus={handleToggleStatus}
                                                        onMoveUp={(p) => handleMoveUp(p, index, categoryProducts)}
                                                        onMoveDown={(p) => handleMoveDown(p, index, categoryProducts)}
                                                        onSortOrderChange={handleSortOrderChange}
                                                        togglingStatus={togglingStatus}
                                                        savingOrder={savingOrder}
                                                        renderProductCard={renderProductCard}
                                                    />
                                                ))}
                                            </div>
                                        </SortableContext>
                                    </DndContext>
                                </div>
                            );
                        })}
                        
                        {uncategorizedProducts.length > 0 && (
                            <div>
                                <div className="flex items-center gap-3 mb-4">
                                    <h2 className="text-lg font-bold text-gray-500">Kategorisiz</h2>
                                    <span className="px-2 py-0.5 bg-gray-100 text-gray-600 text-sm rounded-full">
                                        {uncategorizedProducts.length} ürün
                                    </span>
                                </div>
                                <DndContext
                                    sensors={sensors}
                                    collisionDetection={closestCenter}
                                    onDragEnd={(event) => {
                                        const { active, over } = event;
                                        if (!over || active.id === over.id) return;

                                        const oldIndex = uncategorizedProducts.findIndex(p => p.id === active.id);
                                        const newIndex = uncategorizedProducts.findIndex(p => p.id === over.id);

                                        if (oldIndex !== -1 && newIndex !== -1) {
                                            handleProductDragEnd(null, uncategorizedProducts, oldIndex, newIndex);
                                        }
                                    }}
                                >
                                    <SortableContext items={uncategorizedProducts.map(p => p.id)} strategy={verticalListSortingStrategy}>
                                        <div className="grid grid-cols-1 gap-4">
                                            {uncategorizedProducts.map((product, index) => (
                                                <SortableProductItem
                                                    key={product.id}
                                                    product={product}
                                                    index={index}
                                                    total={uncategorizedProducts.length}
                                                    onEdit={(p) => {
                                                        setEditingProduct(p);
                                                        setShowModal(true);
                                                    }}
                                                    onDelete={openDeleteModal}
                                                    onToggleStatus={handleToggleStatus}
                                                    onMoveUp={(p) => handleMoveUp(p, index, uncategorizedProducts)}
                                                    onMoveDown={(p) => handleMoveDown(p, index, uncategorizedProducts)}
                                                    onSortOrderChange={handleSortOrderChange}
                                                    togglingStatus={togglingStatus}
                                                    savingOrder={savingOrder}
                                                    renderProductCard={renderProductCard}
                                                />
                                            ))}
                                        </div>
                                    </SortableContext>
                                </DndContext>
                            </div>
                        )}
                    </div>
                ) : viewMode === 'grid' ? (
                    // Grid View - 2 columns fixed with drag and drop (category-based sorting)
                    <div className="space-y-8">
                        {productsByCategory.map(({ category, products: categoryProducts }) => {
                            const productIds = categoryProducts.map(p => p.id);
                            
                            const handleDragEnd = (event) => {
                                const { active, over } = event;
                                if (!over || active.id === over.id) return;

                                const oldIndex = categoryProducts.findIndex(p => p.id === active.id);
                                const newIndex = categoryProducts.findIndex(p => p.id === over.id);

                                if (oldIndex !== -1 && newIndex !== -1) {
                                    handleProductDragEnd(category.id, categoryProducts, oldIndex, newIndex);
                                }
                            };

                            return (
                                <div key={category.id}>
                                    <div className="flex items-center gap-3 mb-4">
                                        <h2 className="text-lg font-bold text-gray-900">{category.name}</h2>
                                        <span className="px-2 py-0.5 bg-gray-100 text-gray-600 text-sm rounded-full">
                                            {categoryProducts.length} ürün
                                        </span>
                                    </div>
                                    <DndContext
                                        sensors={sensors}
                                        collisionDetection={closestCenter}
                                        onDragEnd={handleDragEnd}
                                    >
                                        <SortableContext items={productIds} strategy={rectSortingStrategy}>
                                            <div className="grid grid-cols-2 gap-4">
                                                {categoryProducts.map((product, index) => (
                                                    <SortableProductItem
                                                        key={product.id}
                                                        product={product}
                                                        index={index}
                                                        total={categoryProducts.length}
                                                        onEdit={(p) => {
                                                            setEditingProduct(p);
                                                            setShowModal(true);
                                                        }}
                                                        onDelete={openDeleteModal}
                                                        onToggleStatus={handleToggleStatus}
                                                        onMoveUp={(p) => handleMoveUp(p, index, categoryProducts)}
                                                        onMoveDown={(p) => handleMoveDown(p, index, categoryProducts)}
                                                        onSortOrderChange={handleSortOrderChange}
                                                        togglingStatus={togglingStatus}
                                                        savingOrder={savingOrder}
                                                        renderProductCard={(p) => renderProductCard(p, true)}
                                                    />
                                                ))}
                                            </div>
                                        </SortableContext>
                                    </DndContext>
                                </div>
                            );
                        })}
                        
                        {uncategorizedProducts.length > 0 && (
                            <div>
                                <div className="flex items-center gap-3 mb-4">
                                    <h2 className="text-lg font-bold text-gray-500">Kategorisiz</h2>
                                    <span className="px-2 py-0.5 bg-gray-100 text-gray-600 text-sm rounded-full">
                                        {uncategorizedProducts.length} ürün
                                    </span>
                                </div>
                                <DndContext
                                    sensors={sensors}
                                    collisionDetection={closestCenter}
                                    onDragEnd={(event) => {
                                        const { active, over } = event;
                                        if (!over || active.id === over.id) return;

                                        const oldIndex = uncategorizedProducts.findIndex(p => p.id === active.id);
                                        const newIndex = uncategorizedProducts.findIndex(p => p.id === over.id);

                                        if (oldIndex !== -1 && newIndex !== -1) {
                                            handleProductDragEnd(null, uncategorizedProducts, oldIndex, newIndex);
                                        }
                                    }}
                                >
                                    <SortableContext items={uncategorizedProducts.map(p => p.id)} strategy={rectSortingStrategy}>
                                        <div className="grid grid-cols-2 gap-4">
                                            {uncategorizedProducts.map((product, index) => (
                                                <SortableProductItem
                                                    key={product.id}
                                                    product={product}
                                                    index={index}
                                                    total={uncategorizedProducts.length}
                                                    onEdit={(p) => {
                                                        setEditingProduct(p);
                                                        setShowModal(true);
                                                    }}
                                                    onDelete={openDeleteModal}
                                                    onToggleStatus={handleToggleStatus}
                                                    onMoveUp={(p) => handleMoveUp(p, index, uncategorizedProducts)}
                                                    onMoveDown={(p) => handleMoveDown(p, index, uncategorizedProducts)}
                                                    onSortOrderChange={handleSortOrderChange}
                                                    togglingStatus={togglingStatus}
                                                    savingOrder={savingOrder}
                                                    renderProductCard={(p) => renderProductCard(p, true)}
                                                />
                                            ))}
                                        </div>
                                    </SortableContext>
                                </DndContext>
                            </div>
                        )}
                    </div>
                ) : (
                    // List View with drag and drop (category-based sorting)
                    <div className="space-y-8">
                        {productsByCategory.map(({ category, products: categoryProducts }) => {
                            const productIds = categoryProducts.map(p => p.id);
                            
                            const handleDragEnd = (event) => {
                                const { active, over } = event;
                                if (!over || active.id === over.id) return;

                                const oldIndex = categoryProducts.findIndex(p => p.id === active.id);
                                const newIndex = categoryProducts.findIndex(p => p.id === over.id);

                                if (oldIndex !== -1 && newIndex !== -1) {
                                    handleProductDragEnd(category.id, categoryProducts, oldIndex, newIndex);
                                }
                            };

                            return (
                                <div key={category.id}>
                                    <div className="flex items-center gap-3 mb-4">
                                        <h2 className="text-lg font-bold text-gray-900">{category.name}</h2>
                                        <span className="px-2 py-0.5 bg-gray-100 text-gray-600 text-sm rounded-full">
                                            {categoryProducts.length} ürün
                                        </span>
                                    </div>
                                    <DndContext
                                        sensors={sensors}
                                        collisionDetection={closestCenter}
                                        onDragEnd={handleDragEnd}
                                    >
                                        <SortableContext items={productIds} strategy={verticalListSortingStrategy}>
                                            <div className="space-y-3">
                                                {categoryProducts.map((product, index) => (
                                                    <SortableProductItem
                                                        key={product.id}
                                                        product={product}
                                                        index={index}
                                                        total={categoryProducts.length}
                                                        onEdit={(p) => {
                                                            setEditingProduct(p);
                                                            setShowModal(true);
                                                        }}
                                                        onDelete={openDeleteModal}
                                                        onToggleStatus={handleToggleStatus}
                                                        onMoveUp={(p) => handleMoveUp(p, index, categoryProducts)}
                                                        onMoveDown={(p) => handleMoveDown(p, index, categoryProducts)}
                                                        onSortOrderChange={handleSortOrderChange}
                                                        togglingStatus={togglingStatus}
                                                        savingOrder={savingOrder}
                                                        renderProductCard={(p) => renderProductRow(p)}
                                                    />
                                                ))}
                                            </div>
                                        </SortableContext>
                                    </DndContext>
                                </div>
                            );
                        })}
                        
                        {uncategorizedProducts.length > 0 && (
                            <div>
                                <div className="flex items-center gap-3 mb-4">
                                    <h2 className="text-lg font-bold text-gray-500">Kategorisiz</h2>
                                    <span className="px-2 py-0.5 bg-gray-100 text-gray-600 text-sm rounded-full">
                                        {uncategorizedProducts.length} ürün
                                    </span>
                                </div>
                                <DndContext
                                    sensors={sensors}
                                    collisionDetection={closestCenter}
                                    onDragEnd={(event) => {
                                        const { active, over } = event;
                                        if (!over || active.id === over.id) return;

                                        const oldIndex = uncategorizedProducts.findIndex(p => p.id === active.id);
                                        const newIndex = uncategorizedProducts.findIndex(p => p.id === over.id);

                                        if (oldIndex !== -1 && newIndex !== -1) {
                                            handleProductDragEnd(null, uncategorizedProducts, oldIndex, newIndex);
                                        }
                                    }}
                                >
                                    <SortableContext items={uncategorizedProducts.map(p => p.id)} strategy={verticalListSortingStrategy}>
                                        <div className="space-y-3">
                                            {uncategorizedProducts.map((product, index) => (
                                                <SortableProductItem
                                                    key={product.id}
                                                    product={product}
                                                    index={index}
                                                    total={uncategorizedProducts.length}
                                                    onEdit={(p) => {
                                                        setEditingProduct(p);
                                                        setShowModal(true);
                                                    }}
                                                    onDelete={openDeleteModal}
                                                    onToggleStatus={handleToggleStatus}
                                                    onMoveUp={(p) => handleMoveUp(p, index, uncategorizedProducts)}
                                                    onMoveDown={(p) => handleMoveDown(p, index, uncategorizedProducts)}
                                                    onSortOrderChange={handleSortOrderChange}
                                                    togglingStatus={togglingStatus}
                                                    savingOrder={savingOrder}
                                                    renderProductCard={(p) => renderProductRow(p)}
                                                />
                                            ))}
                                        </div>
                                    </SortableContext>
                                </DndContext>
                            </div>
                        )}
                    </div>
                )}

                {/* Product Form Modal */}
                <ProductFormModal
                    isOpen={showModal}
                    onClose={() => {
                        setShowModal(false);
                        setEditingProduct(null);
                    }}
                    product={editingProduct}
                    organizationId={organizationId}
                    categoryId={selectedCategory !== 'all' ? selectedCategory : null}
                    onSuccess={loadProducts}
                />

                {/* Delete Confirmation Modal */}
                <DeleteConfirmModal
                    isOpen={deleteModal.isOpen}
                    onClose={closeDeleteModal}
                    onConfirm={handleConfirmDelete}
                    productName={deleteModal.product?.name}
                    loading={deleting}
                />
            </div>
        </AdminLayout>
    );
};

export default ProductsPage;
